// answerKeys.js
// The ids of a batch's question documents and the keys of the evidence their
// answers keep (Spec 3 section 2.1), computed exactly as the backend computes
// them (functions/ingestion/walk/subjects.py): the browser names a question by
// its subject's id and stores each snapshot an answer is bound to under its key.
//
// Both are digests of one canonical JSON text: UTF-8, object keys in code-point
// order, no optional whitespace, Unicode unescaped, every null kept. A number is
// rounded to four decimal places on its exact binary value, an exact tie going
// to the even neighbour, and written as an integer, in full, when the rounded
// value is integral, so 10.0 and 10 encode alike and 1e21 is never written with
// an exponent. The golden vectors in functions/tests/unit/ingestion/walk/
// subject_vectors.json hold this file and subjects.py together.
//
// A key says where a snapshot is kept; it never shows that the snapshot is the
// backend's own (promotion checks that).

// BigInt writes an integral number's digits exactly; the project's lint
// configuration predates it as a global.
/* global BigInt */

const ROW_SUBJECT_FIELDS = ['kind', 'file_id', 'unit_id', 'row', 'period_label', 'page', 'record'];
const BELIEF_SUBJECT_FIELDS = ['kind', 'identifier', 'data_profile_id'];
const EVIDENCE_FIELDS = ['ref', 'identifier', 'details', 'data_profile_id', 'attributes'];

// The canonical JSON text of a value made of objects, arrays, text, numbers,
// booleans and nulls. Anything else has no canonical form, as the backend's
// json.dumps has none for it, and throws.
export function canonicalJson(value) {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('Identity evidence cannot contain non-finite numbers.');
    if (Number.isInteger(value)) return BigInt(value).toString();
    // toFixed rounds the exact binary value to four decimals, but takes the
    // larger neighbour on an exact tie, which the backend's round() takes to the
    // even one. A tie at the fourth decimal is a value that is an odd multiple
    // of 1/32 (0.03125, 0.09375, ...), and multiplying by 32 is exact.
    const magnitude = Math.abs(value);
    let tenThousandths = BigInt(magnitude.toFixed(4).replace('.', ''));
    const exactTie = Number.isInteger(magnitude * 32) && (magnitude * 32) % 2 === 1;
    if (exactTie && tenThousandths % 2n === 1n) tenThousandths -= 1n;
    const whole = tenThousandths / 10000n;
    const fraction = (tenThousandths % 10000n).toString().padStart(4, '0');
    const rounded = Number(`${value < 0 ? '-' : ''}${whole}.${fraction}`);
    // The shortest text that reads back as the rounded number, as the
    // backend's repr() writes it; integral after rounding, written in full.
    return Number.isInteger(rounded) ? BigInt(rounded).toString() : String(rounded);
  }
  if (Array.isArray(value)) return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    // Code-point order, as the backend sorts: plain string comparison orders
    // by UTF-16 unit, which puts an emoji before a full-width letter.
    const keys = Object.keys(value).sort((left, right) => {
      const leftPoints = Array.from(left, (character) => character.codePointAt(0));
      const rightPoints = Array.from(right, (character) => character.codePointAt(0));
      for (let index = 0; index < Math.min(leftPoints.length, rightPoints.length); index += 1) {
        if (leftPoints[index] !== rightPoints[index]) return leftPoints[index] - rightPoints[index];
      }
      return leftPoints.length - rightPoints.length;
    });
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(',')}}`;
  }
  throw new Error('This value has no canonical JSON form.');
}

// Whether `value` has exactly the fields `names`, no more and no fewer.
function hasExactly(value, names) {
  const keys = Object.keys(value);
  return keys.length === names.length && names.every((name) => Object.hasOwn(value, name));
}

// The lowercase hexadecimal digest `algorithm` ('SHA-1', 'SHA-256') of a text's UTF-8 bytes.
async function hexDigest(algorithm, text) {
  const digest = await crypto.subtle.digest(algorithm, new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// A question's document id: the SHA-1 of its subject's canonical JSON. The
// subject must carry exactly its kind's fields, absent provenance included as
// null, or the id would depend on how a caller spelled it.
//
//   a row            { kind: 'row', file_id, unit_id, row, period_label, page, record }
//   a belief source  { kind: 'belief_source', identifier, data_profile_id }
export async function subjectQid(subject) {
  let fields;
  if (subject?.kind === 'row') fields = ROW_SUBJECT_FIELDS;
  else if (subject?.kind === 'belief_source') fields = BELIEF_SUBJECT_FIELDS;
  else throw new Error('Unknown assignment subject.');
  if (!hasExactly(subject, fields)) throw new Error('A subject needs every canonical field, including explicit nulls.');
  return hexDigest('SHA-1', canonicalJson(subject));
}

// The key a snapshot of a row (a RowEvidence: { ref, identifier, details,
// data_profile_id, attributes }) is kept under in an answer's relevant_rows:
// the SHA-256 of its canonical JSON.
export async function evidenceKey(evidence) {
  if (evidence === null || typeof evidence !== 'object' || !hasExactly(evidence, EVIDENCE_FIELDS)) {
    throw new Error('Expected complete RowEvidence.');
  }
  return hexDigest('SHA-256', canonicalJson(evidence));
}
